import {
  Redirect,
  Route,
  Switch,
  useRouteMatch,
  useLocation,
  useHistory,
} from "react-router-dom";
import { useQueryParam } from "~/common/hooks/useQueryParams";
import ProfileTypeSelector from "../../CaseDetailsDrawer/PaymentInstructions/PaymentProfileCreationComponent/ProfileTypeSelector";
import { BottomUpDrawer } from "../../Common/BottomUpDrawer";
import { useCurrentCaseContext } from "../../context/CurrentCaseContext";
import { useGuidedProfileCreation } from "../../flags";
import { ProfileCreationPreview } from "../ProfileCreationPreview";
import { ProfileEditPreview } from "../ProfileEditPreview";
import { ProfileHierarchyTable } from "../ProfileHierarchyTable";
import { ProfileResolverPreview } from "./ProfileResolverPreview";
import { getPageHeader, PATH_SEGMENTS } from "./utils";

/**
 * Routes profile flows from Document Management. ↔ = two-way, → = one-way.
 *
 * Profile information conflict:
 *   DocumentManagement ↔ ProfileTypeSelector
 *   Path: .../upsert-profile/conflict-resolver?documentId=19&profileId=88
 *
 * Profile creation:
 *   DocumentManagement ↔ ProfileTypeSelector ↔ ProfileCreation → ProfileHierarchyTable ↔ ProfileEditPreview
 *   .../upsert-profile?documentId=19
 *   .../upsert-profile/entity?documentId=19
 *   .../upsert-profile/entity/profiles?documentId=19&profileId=88
 *   .../upsert-profile/entity/profiles/new_edit?documentId=19&profileId=88
 *   Exit: .../document-management?documentId=19
 *
 * Edit existing profile:
 *   DocumentManagement ↔ ProfileEditPreview ↔ ProfileHierarchyTable → DocumentManagement
 *   .../upsert-profile/individual/edit-profile?documentId=19&profileId=88
 *   .../upsert-profile/individual/edit-profile/profiles?documentId=19&profileId=88
 *   Exit: .../document-management?documentId=19
 */
export const DocManagementProfileCreation = ({
  investorProfileId,
}: {
  investorProfileId?: number;
}) => {
  const history = useHistory();
  const match = useRouteMatch();
  const location = useLocation();
  const { isJointRootProfile } = useCurrentCaseContext();
  const guidedProfileCreation = useGuidedProfileCreation();
  // Joint roots use a streamlined flow: no individual/entity selector and no
  // profile hierarchy step (they only allow individual owner children).
  const isJointRootFlow = guidedProfileCreation && isJointRootProfile;
  const allowHierarchy = !isJointRootFlow;
  const [docId] = useQueryParam("documentId");
  const [profId] = useQueryParam("profileId");
  const documentId = Number(docId);
  const profileId = Number(profId);
  const basePath = location.pathname.split(PATH_SEGMENTS.rootPath)[0];
  const onCloseUrl = `${basePath}${documentId ? `?documentId=${documentId}` : ""}`;
  const currentPageUrl = `${basePath}${PATH_SEGMENTS.rootPath}${documentId ? `?documentId=${documentId}` : ""}`;

  /**
   * Launches the Edit flow from {@link ProfileHierarchyTable} (profile creation scenario).
   */
  const launchNewProfileEdit = () => {
    history.push({
      pathname: `${location.pathname}${PATH_SEGMENTS.editNewProfile}`,
      search: location.search,
    });
  };

  /**
   * Returns to {@link ProfileHierarchyTable} from {@link ProfileEditPreview} (profile creation scenario).
   */
  const relaunchProfilesAfterEdit = () => {
    history.push({
      pathname: location.pathname.split(PATH_SEGMENTS.editNewProfile)[0],
      search: location.search,
    });
  };

  /**
   * Navigates to {@link ProfileHierarchyTable} (creation and edit scenarios).
   */
  const launchProfiles = (selectedProfileId?: number) => {
    const params = new URLSearchParams(location.search);
    params.set("profileId", String(selectedProfileId ?? profileId));
    history.push({
      pathname: `${location.pathname}${PATH_SEGMENTS.profiles}`,
      search: params.toString(),
    });
  };

  /**
   * Returns to {@link ProfileEditPreview} from {@link ProfileHierarchyTable} (edit scenario).
   */
  const launchEdit = () => {
    history.push({
      pathname: location.pathname.split(PATH_SEGMENTS.profiles)[0],
      search: location.search,
    });
  };

  /**
   * Navigates back to {@link ProfileTypeSelector} (profile creation scenario).
   * When the hierarchy step is skipped (joint root) there is no selector to
   * return to, so it goes back to Document Management instead.
   */
  const launchPageBasepath = () => {
    history.push(allowHierarchy ? currentPageUrl : onCloseUrl);
  };

  /**
   * Closes the flow and navigates back to {@link DocumentManagement}.
   */
  const onClose = () => {
    history.push(onCloseUrl);
  };

  const headerText = getPageHeader(location.pathname, isJointRootFlow);

  return (
    <BottomUpDrawer
      isShowing
      headerText={headerText}
      navigationLabel="Back to Document management"
      onCloseUrl={onCloseUrl}
    >
      <Switch>
        <Route path={`${match.path}${PATH_SEGMENTS.conflictResolver}`} exact>
          <ProfileResolverPreview
            profileId={profileId}
            documentId={documentId}
            onClose={onClose}
          />
        </Route>
        <Route path={`${match.path}/:profileType(individual|entity)`} exact>
          <ProfileCreationPreview
            parentProfileId={investorProfileId}
            isJointChild={isJointRootFlow}
            onCancel={launchPageBasepath}
            onComplete={allowHierarchy ? launchProfiles : onClose}
          />
        </Route>
        <Route
          path={`${match.path}/:profileType(individual|entity)${PATH_SEGMENTS.profiles}`}
          exact
        >
          <ProfileHierarchyTable onCancel={launchNewProfileEdit} onComplete={onClose} />
        </Route>
        <Route
          path={`${match.path}/:profileType(individual|entity)${PATH_SEGMENTS.profiles}${PATH_SEGMENTS.editNewProfile}`}
          exact
        >
          <ProfileEditPreview onComplete={relaunchProfilesAfterEdit} />
        </Route>
        <Route
          path={`${match.path}/:profileType(individual|joint|entity)${PATH_SEGMENTS.editProfile}`}
          exact
        >
          <ProfileEditPreview
            allowHierarchy={allowHierarchy}
            onComplete={allowHierarchy ? launchProfiles : onClose}
          />
        </Route>
        <Route
          path={`${match.path}/:profileType(individual|joint|entity)${PATH_SEGMENTS.editProfile}${PATH_SEGMENTS.profiles}`}
          exact
        >
          <ProfileHierarchyTable onCancel={launchEdit} onComplete={onClose} />
        </Route>
        <Route path={`${match.path}`}>
          {allowHierarchy ? (
            <ProfileTypeSelector />
          ) : (
            // Joint roots only allow individual owner children, so skip the
            // individual/entity selector and go straight to the individual form.
            <Redirect
              to={{
                pathname: `${basePath}${PATH_SEGMENTS.rootPath}/individual`,
                search: location.search,
              }}
            />
          )}
        </Route>
      </Switch>
    </BottomUpDrawer>
  );
};
